import React from "react";
import {
  Bar,
  BarChart,
  CartesianGrid,
  Cell,
  LabelList,
  ResponsiveContainer,
  Tooltip,
  XAxis,
  YAxis,
} from "recharts";
import ChartTooltip from "./ChartTooltip";
import {
  MAX_BAR_SIZE,
  axisProps,
  barRadius,
  chrome,
  labelProps,
  seriesColor,
  NEUTRAL,
} from "./chartTheme";

/**
 * Single-series distribution bars, either as columns (`orientation="column"`)
 * or as horizontal bars (`orientation="bar"`).
 *
 * One series means one colour - the bar length already encodes magnitude, so
 * painting each bar a different hue would encode nothing. The only exception is
 * a residual bucket ("Others", "N/A"), which is a distinct kind of entity and
 * takes the reserved neutral.
 *
 * @param {{name: string, value: number}[]} data
 * @param {string[]} neutralKeys names drawn in the reserved neutral
 */
export default function BarDistributionChart({
  data = [],
  orientation = "column",
  height = 240,
  categoryWidth = 120,
  valueAxisLabel,
  total,
  colorFor,
  neutralKeys = [],
  showValues = true,
}) {
  const theme = chrome();
  const isBar = orientation === "bar";
  const axis = axisProps(theme);

  const fillFor = (entry, index) => {
    if (colorFor) return colorFor(entry, index);
    if (neutralKeys.includes(entry.name)) return NEUTRAL();
    return seriesColor(0);
  };

  return (
    <ResponsiveContainer width="100%" height={height}>
      <BarChart
        data={data}
        layout={isBar ? "vertical" : "horizontal"}
        margin={{
          /* A column that reaches the axis max puts its value label above the
             plot top - reserve the label's height so it can't be clipped. */
          top: !isBar && showValues ? 24 : 12,
          right: isBar ? 40 : 12,
          /* Horizontal bars put the value axis - and its label - along the
             bottom, so it needs the room; columns label the left axis. */
          bottom: isBar && valueAxisLabel ? 20 : 0,
          left: isBar ? 0 : 4,
        }}
        barCategoryGap="28%"
      >
        <CartesianGrid
          stroke={theme.grid}
          horizontal={!isBar}
          vertical={isBar}
        />

        {isBar ? (
          <>
            <XAxis
              type="number"
              {...axis}
              label={
                valueAxisLabel
                  ? {
                      value: valueAxisLabel,
                      position: "insideBottom",
                      offset: -4,
                      fill: theme.muted,
                      fontSize: 11,
                    }
                  : undefined
              }
            />
            <YAxis
              type="category"
              dataKey="name"
              width={categoryWidth}
              {...axis}
            />
          </>
        ) : (
          <>
            <XAxis type="category" dataKey="name" {...axis} interval={0} />
            <YAxis
              type="number"
              {...axis}
              label={
                valueAxisLabel
                  ? {
                      value: valueAxisLabel,
                      angle: -90,
                      position: "insideLeft",
                      fill: theme.muted,
                      fontSize: 11,
                    }
                  : undefined
              }
            />
          </>
        )}

        <Tooltip
          cursor={{ fill: theme.grid, fillOpacity: 0.5 }}
          content={<ChartTooltip total={total} />}
        />

        <Bar
          dataKey="value"
          name="Applicants"
          maxBarSize={MAX_BAR_SIZE}
          radius={barRadius(isBar ? "vertical" : "horizontal")}
          isAnimationActive={false}
        >
          {data.map((entry, index) => (
            <Cell key={entry.name} fill={fillFor(entry, index)} />
          ))}
          {showValues && (
            <LabelList
              dataKey="value"
              position={isBar ? "right" : "top"}
              {...labelProps(theme)}
            />
          )}
        </Bar>
      </BarChart>
    </ResponsiveContainer>
  );
}
